'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Play } from 'lucide-react';
import { VideoSectionComponent } from '@/types/product';
import MediaWithFallback from '@/components/ui/MediaWithFallback';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import { cn } from '@/utils/cn';

interface WitnessFarqEngineeringProps {
  videoComponent?: VideoSectionComponent;
  theme?: 'dark' | 'light';
  sectionClassName?: string;
}

export default function WitnessFarqEngineering({
  videoComponent,
  theme = 'dark',
  sectionClassName,
}: WitnessFarqEngineeringProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useLayoutEffect(() => {
    const container = mediaContainerRef.current;
    if (!container) return;
    let video = container.querySelector('video');
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const attach = (v: HTMLVideoElement) => {
      v.addEventListener('play', onPlay);
      v.addEventListener('playing', onPlay);
      v.addEventListener('pause', onPause);
      v.addEventListener('ended', onPause);
      setIsPlaying(!v.paused && !v.ended);
    };
    if (video) attach(video);
    const observer = new MutationObserver(() => {
      const next = container.querySelector('video');
      if (next && next !== video) {
        video = next;
        attach(next);
      }
    });
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (video) {
        video.removeEventListener('play', onPlay);
        video.removeEventListener('playing', onPlay);
        video.removeEventListener('pause', onPause);
        video.removeEventListener('ended', onPause);
      }
    };
  }, []);

  const playVideo = useCallback(() => {
    const container = mediaContainerRef.current;
    const video = container?.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.play().catch(console.warn);
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const section = sectionRef.current;
    const mediaShell = mediaContainerRef.current;
    if (!section || !mediaShell) return;

    gsap.registerPlugin(ScrollTrigger);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      if (prefersReducedMotion) {
        gsap.set(mediaShell, { scale: 1, borderRadius: 0, clearProps: 'willChange' });
        ScrollTrigger.create({
          trigger: section,
          start: 'top 70%',
          onEnter: playVideo,
          onEnterBack: playVideo,
        });

        return;
      }

      gsap.set(mediaShell, {
        scale: 0.75,
        borderRadius: 24,
        transformOrigin: '50% 50%',
        willChange: 'transform',
      });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: mediaShell,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.8,
            invalidateOnRefresh: true,
            onEnter: playVideo,
            onEnterBack: playVideo,
          },
        })
        .to(mediaShell, { scale: 1, borderRadius: 0, ease: 'none', duration: 0.35 })
        .to(mediaShell, { scale: 1, borderRadius: 0, ease: 'none', duration: 0.65 });
    }, section);

    const raf = window.requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => {
      window.cancelAnimationFrame(raf);
      ctx.revert();
    };
  }, [playVideo]);

  const defaultPosterUrl = '/images/aboutImg.png';
  const isLight = theme === 'light';
  const sectionClass = cn(
    isLight ? 'bg-white pb-0 text-black' : 'bg-black pb-0 text-white',
    sectionClassName,
  );
  const defaultTextClass = isLight ? 'text-black' : 'text-white';
  const videoShellClass = 'relative overflow-hidden rounded-2xl';
  const overlayClass = isLight
    ? 'pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-black/8 to-transparent'
    : 'pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent';

  const renderTitle = () => {
    // Normalise CMS heading parts: trim each `text` defensively (CMS data
    // sometimes carries trailing/leading spaces, which combined with the
    // join-space below produced double-spaced output), then drop any parts
    // that are empty after trimming so a heading of all-blank strings still
    // falls through to the hardcoded fallback below.
    const parts = (videoComponent?.heading ?? [])
      .map((p) => ({ ...p, text: (p.text ?? '').trim() }))
      .filter((p) => p.text.length > 0);

    const renderHeadingPart = (
      headingPart: { text: string; textColor?: string | null },
      index: number,
    ) => {
      const isAccent =
        headingPart.textColor === '#ED1C22' ||
        headingPart.textColor?.toUpperCase() === '#E31E24' ||
        headingPart.text.toLowerCase().includes('farq');
      let headingPartClass: string;
      if (isAccent) {
        headingPartClass = 'italic text-[#E82127]';
      } else if (headingPart.textColor && !isAccent) {
        headingPartClass = '';
      } else {
        headingPartClass = defaultTextClass;
      }
      return (
        <span
          key={index}
          style={headingPart.textColor && !isAccent ? { color: headingPart.textColor } : undefined}
          className={headingPartClass}
        >
          {headingPart.text}
          {index < parts.length - 1 ? ' ' : ''}
        </span>
      );
    };

    if (parts.length > 0) {
      return (
        <span className="inline-block max-w-5xl text-balance">{parts.map(renderHeadingPart)}</span>
      );
    }

    // Hardcoded fallback — renders when the CMS hasn't supplied a heading
    // for the current locale (e.g., Hindi pages today). Kept intentionally
    // so the section never appears blank.
    return (
      <>
        <span className={`block leading-[1.12] ${defaultTextClass}`}>
          Witness the <span className="italic text-[#E82127]">Farq</span> with Our
        </span>
        <span className={`block leading-[1.12] ${defaultTextClass}`}>World-Class Engineering</span>
      </>
    );
  };

  const getVideoUrl = (): string | undefined => {
    if (videoComponent?.sectionVideo?.[0]?.url) {
      const resolved = resolveStrapiMediaUrl(videoComponent.sectionVideo[0].url);
      if (resolved) return resolved;
    }
    return undefined;
  };

  const getPosterUrl = (): string => {
    if (videoComponent?.videoPoster?.url) {
      const resolved = resolveStrapiMediaUrl(videoComponent.videoPoster.url);
      if (resolved) return resolved;
    }
    return defaultPosterUrl;
  };

  const toggleVideoPlayback = useCallback(() => {
    const container = mediaContainerRef.current;
    const video = container?.querySelector('video');
    if (!video) return;

    if (video.paused) {
      video.play().catch(console.error);
    } else {
      video.pause();
    }
  }, []);

  return (
    <section ref={sectionRef} className={`${sectionClass} overflow-hidden`}>
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8">
        <h3
          className={`mx-auto flex max-w-5xl flex-col items-center gap-1 text-center text-3xl font-semibold tracking-tight md:gap-1.5 md:text-4xl md:font-bold lg:text-5xl lg:leading-tight ${defaultTextClass}`}
        >
          {renderTitle()}
        </h3>
      </div>

      <div className="mt-8 sm:mt-10 md:mt-14 lg:mt-16">
        <div ref={mediaContainerRef} className={cn(videoShellClass, 'relative w-full')}>
          <div className="relative h-[min(72vw,400px)] w-full min-h-[240px] sm:min-h-[280px] md:h-[min(56vw,450px)] lg:h-[620px]">
            <MediaWithFallback
              videoUrl={getVideoUrl()}
              imageUrl={getPosterUrl()}
              alt={videoComponent?.videoPoster?.alternativeText || 'Engineering video poster'}
              className="h-full w-full"
              imageClassName="object-cover object-center"
              videoClassName="object-cover object-center"
              fill
              sizes="(min-width: 1536px) 1536px, 100vw"
              autoPlay={false}
              muted
              loop={true}
              playsInline
              poster={getPosterUrl()}
              showFirstFrame
            />
            <div className={overlayClass} aria-hidden />
          </div>

          <button
            type="button"
            className="absolute inset-0 grid cursor-pointer place-items-center"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
            onClick={(e) => {
              e.preventDefault();
              toggleVideoPlayback();
            }}
          >
            <span
              className={`relative flex h-[4.5rem] w-[4.5rem] items-center justify-center transition-opacity duration-300 md:h-[5.25rem] md:w-[5.25rem] ${
                isPlaying ? 'opacity-0' : 'opacity-100'
              }`}
              aria-hidden={isPlaying}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="animate-play-ripple-ring pointer-events-none absolute inset-0 rounded-full border-[6px] border-white/45 md:border-8 md:border-white/50"
                  style={{ animationDelay: `${i * 0.75}s` }}
                  aria-hidden
                />
              ))}
              <span className="relative z-10 flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full border border-white/50 bg-white/20 shadow-[0_0_24px_rgba(255,255,255,0.12)] backdrop-blur-[2px] md:h-[5.25rem] md:w-[5.25rem]">
                <Play className="ml-1 h-8 w-8 fill-white text-white md:h-9 md:w-9" />
              </span>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
